import { useState, useEffect, FormEvent } from 'react';
import { apiService } from '../services/api';
import { MFAStatusResponse, MFAChangeOtpStartResponse } from '../types/api.types';
import MFAEmailConfirm from './MFAEmailConfirm';

// MFA method management (issue #213): view current method, test the authenticator,
// and switch between "Email + Authenticator" and "Email only" with an emailed
// step-up code.
export default function MFASettings() {
  const [status, setStatus] = useState<MFAStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState<'view' | 'to-email' | 'to-otp'>('view');
  const [emailStepUp, setEmailStepUp] = useState<{ expiresIn: number; emailMasked: string } | null>(null);
  const [otpStart, setOtpStart] = useState<MFAChangeOtpStartResponse | null>(null);

  const load = async () => {
    try {
      setStatus(await apiService.getMFAStatusFull());
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const resetToView = () => {
    setMode('view');
    setEmailStepUp(null);
    setOtpStart(null);
    setError('');
  };

  const startToEmail = async () => {
    setError('');
    setSuccess('');
    try {
      const r = await apiService.mfaChangeSendCode();
      setEmailStepUp({ expiresIn: r.expires_in, emailMasked: r.email_masked });
      setMode('to-email');
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    }
  };

  const confirmToEmail = async (code: string) => {
    await apiService.mfaChangeToEmail(code);
    resetToView();
    setSuccess('Switched to email-only MFA.');
    await load();
  };

  const startToOtp = async () => {
    setError('');
    setSuccess('');
    try {
      setOtpStart(await apiService.mfaChangeToOtpStart());
      setMode('to-otp');
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  if (mode === 'to-email' && emailStepUp) {
    return (
      <div className="lg:col-span-2">
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
          You're removing your authenticator app. After this you'll sign in with an emailed code.
        </div>
        <MFAEmailConfirm
          title="Confirm switch to email-only"
          emailMasked={emailStepUp.emailMasked}
          expiresIn={emailStepUp.expiresIn}
          onVerify={confirmToEmail}
          onResend={async () => {
            const r = await apiService.mfaChangeSendCode();
            setEmailStepUp({ expiresIn: r.expires_in, emailMasked: r.email_masked });
            return r.expires_in;
          }}
          onBack={resetToView}
          backLabel="Cancel"
        />
      </div>
    );
  }

  if (mode === 'to-otp' && otpStart) {
    return (
      <ToOtpForm
        start={otpStart}
        onCancel={resetToView}
        onDone={async () => {
          resetToView();
          setSuccess('Authenticator enabled.');
          await load();
        }}
      />
    );
  }

  const methodLabel =
    status?.mfa_method === 'email_otp'
      ? 'Email + Authenticator app'
      : status?.mfa_method === 'email'
        ? 'Email only'
        : '—';

  // Rendered as grid cells inside ProfilePanel's Security card (Change password |
  // Change MFA type | Test MFA device). The lg:border-l acts as the column divider.
  return (
    <>
      {/* Change MFA type (tile 2) */}
      <div className="border-t pt-6 border-gray-200 lg:border-t-0 lg:pt-0 lg:border-l lg:pl-6">
        <h4 className="text-base font-bold text-gray-900 mb-3">Change MFA type</h4>
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>}
        {success && <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{success}</div>}
        <label className="block text-sm font-medium text-gray-500">Current method</label>
        <p className="text-gray-900 mb-4">{methodLabel}</p>
        {status?.mfa_method === 'email_otp' && (
          <button onClick={startToEmail} className="btn-app btn-app--primary">
            Switch to Email only
          </button>
        )}
        {status?.mfa_method === 'email' && (
          <button onClick={startToOtp} className="btn-app btn-app--primary">
            Switch to Email + Authenticator
          </button>
        )}
      </div>

      {/* Test MFA device (tile 3) */}
      {status?.has_otp && (
        <div className="border-t pt-6 border-gray-200 lg:border-t-0 lg:pt-0 lg:border-l lg:pl-6">
          <TestAuthenticator />
        </div>
      )}
    </>
  );
}

function ToOtpForm({
  start,
  onCancel,
  onDone,
}: {
  start: MFAChangeOtpStartResponse;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [totp, setTotp] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (totp.length !== 6 || emailCode.length !== 6) {
      setError('Enter both 6-digit codes');
      return;
    }
    setLoading(true);
    try {
      const r = await apiService.mfaChangeToOtpComplete({
        change_token: start.change_token,
        totp_code: totp,
        email_code: emailCode,
      });
      if (r.backup_codes && r.backup_codes.length) setBackupCodes(r.backup_codes);
      else onDone();
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (backupCodes) {
    return (
      <div className="lg:col-span-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Save your backup codes</h2>
        <p className="text-sm text-gray-600 mb-4">
          Store these somewhere safe. Each can be used once if you lose your authenticator.
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 rounded p-3 mb-4">
          {backupCodes.map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
        <button onClick={onDone} className="w-full btn-app btn-app--primary">
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="lg:col-span-2">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Add an authenticator app</h2>
      <p className="text-sm text-gray-600 mb-4">
        Scan the QR code with your authenticator app, then enter both codes to confirm.
      </p>
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>}
      <div className="flex justify-center mb-3">
        <img
          src={start.qr_code_data}
          alt="MFA QR Code"
          className="border border-gray-200 rounded p-2"
          style={{ width: 180, height: 180 }}
        />
      </div>
      <div className="text-center mb-4">
        <button type="button" onClick={() => setShowSecret(!showSecret)} className="text-sm text-blue-600 hover:underline">
          {showSecret ? 'Hide' : 'Show'} manual key
        </button>
        {showSecret && <p className="mt-1 font-mono text-xs break-all">{start.secret_key}</p>}
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Code from authenticator app</label>
          <input
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            className="w-full px-3 py-2 border border-gray-300 rounded text-center tracking-widest"
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Code emailed to {start.email_masked}
          </label>
          <input
            value={emailCode}
            onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            autoComplete="one-time-code"
            className="w-full px-3 py-2 border border-gray-300 rounded text-center tracking-widest"
            disabled={loading}
          />
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading || totp.length !== 6 || emailCode.length !== 6}
            className="btn-app btn-app--primary"
          >
            {loading ? 'Enabling…' : 'Enable authenticator'}
          </button>
          <button type="button" onClick={onCancel} className="btn-app btn-app--neutral">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function TestAuthenticator() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    setLoading(true);
    try {
      const r = await apiService.testAuthenticator(code);
      setResult(r.valid);
    } catch {
      setResult(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h4 className="text-base font-bold text-gray-900 mb-3">Test MFA device</h4>
      <p className="text-sm text-gray-600 mb-3">Enter a code from your authenticator app to confirm it still works.</p>
      <form onSubmit={submit}>
        <div className="flex items-center gap-3">
          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setResult(null);
            }}
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            className="px-3 py-2 border border-gray-300 rounded text-center tracking-widest w-32"
            disabled={loading}
          />
          {result !== null && (
            <p className={`text-sm ${result ? 'text-green-700' : 'text-red-700'}`}>
              {result ? '✓ Valid code' : '✗ Invalid code'}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="btn-app btn-app--primary mt-3 w-32"
        >
          Test
        </button>
      </form>
    </div>
  );
}
